// src/components/admin/AdminLayout.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  FiHome,
  FiPackage,
  FiShoppingBag,
  FiUsers,
  FiTag,
  FiAward,
  FiBarChart2,
  FiMessageSquare,
  FiDollarSign,
  FiTrendingUp,
  FiMail,
  FiLogOut,
  FiBell,
  FiMenu,
  FiX,
  FiMapPin,
  FiCreditCard,
  FiLayers,
  FiSettings,
  FiUser,
  FiShield,
  FiActivity,
  FiDownloadCloud,
  FiLifeBuoy,
  FiAlertTriangle,
  FiImage,
  FiCode,
  FiGift
, FiRotateCcw, FiShare2, FiTrendingDown, FiExternalLink, FiFileText, FiAlertCircle ,
  FiLock
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API, { merchantAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { normalizeImagePath } from '../../utils/imageUtils';
import GlobalSearch from '../common/GlobalSearch';
import ProfileMenu from '../common/ProfileMenu';
import GlowButton from '../ui/GlowButton';
import SidebarProfileCard from '../common/SidebarProfileCard';

const AdminLayout = () => {
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const brandLogo = settings?.logo ? normalizeImagePath(settings.logo) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Densité d'affichage de l'admin (zoom global, persisté). 0.8 = comme dézoomé.
  const [adminZoom, setAdminZoom] = useState(() => {
    try { return localStorage.getItem('admin_zoom') || '0.8'; } catch (e) { return '0.8'; }
  });
  const changeZoom = (v) => {
    setAdminZoom(v);
    try { localStorage.setItem('admin_zoom', v); } catch (e) {}
  };
  const [unreadCount, setUnreadCount] = useState(0);
  const [storeRef, setStoreRef] = useState(null); // boutique publique de l'admin

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await merchantAPI.getProfile();
        const m = (r && r.data && (r.data.data || r.data.merchant || r.data)) || {};
        const ref = m.slug || m.id || m._id;
        if (alive && ref) setStoreRef(ref);
      } catch (e) { /* pas de boutique -> bouton masque */ }
    })();
    return () => { alive = false; };
  }, []);
  const [badges, setBadges] = useState({ merchants: 0, verifications: 0, tickets: 0, reports: 0, refunds: 0, withdrawals: 0, subscriptions: 0, unverifiedEmails: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  const menuItems = [
    { path: '/admin', icon: FiHome, label: 'Tableau de bord', exact: true },
    { path: '/admin/deals', icon: FiPackage, label: 'Gestion des Deals' },
    { path: '/admin/orders', icon: FiShoppingBag, label: 'Commandes' },
    { path: '/admin/users', icon: FiUsers, label: 'Utilisateurs' },
    // Accès du personnel : rôles, permissions et portefeuille de marchands.
    { path: '/admin/staff', icon: FiShield, label: 'Accès du personnel' },
    { path: '/admin/roles', icon: FiLock, label: 'Rôles & Permissions' },
    { path: '/admin/merchants', icon: FiTag, label: 'Marchands' },
    // File de relance : comptes dont l'email n'est jamais confirme. Sans email
    // verifie, les annonces du marchand ne peuvent pas etre publiees.
    { path: '/admin/unverified-emails', icon: FiMail, label: 'Emails non vérifiés' },
    { path: '/admin/verifications', icon: FiShield, label: 'Vérifications' },
    { path: '/admin/tickets', icon: FiLifeBuoy, label: 'Tickets' },
    { path: '/admin/reports', icon: FiAlertTriangle, label: 'Signalements' },
    // v1.466.0 — fiches clients (fidelite). Placee juste apres Leads : les
    // deux repondent a la meme question, « qui sont mes contacts ».
    { path: '/admin/clients', icon: FiUsers, label: 'Clients' },
    { path: '/admin/leads', icon: FiUsers, label: 'Leads' },
    { path: '/admin/ads', icon: FiImage, label: 'Publicités' },
    { path: '/admin/api', icon: FiCode, label: 'API & Webhooks' },
    { path: '/admin/plans', icon: FiLayers, label: 'Packs' },
    { path: '/admin/subscriptions', icon: FiCreditCard, label: 'Abonnements' },
    { path: '/admin/vouchers', icon: FiTag, label: 'Vouchers' },
    { path: '/admin/promos', icon: FiTag, label: 'Codes Promo' },
    { path: '/admin/rewards', icon: FiGift, label: 'Récompenses' },
    { path: '/admin/categories', icon: FiTag, label: 'Catégories' },
    // Catalogue des marques (nom + logo) propose aux marchands dans le
    // formulaire d'annonce.
    { path: '/admin/brands', icon: FiAward, label: 'Marques' },

    // ✅ ADDED: Cities
    { path: '/admin/cities', icon: FiMapPin, label: 'Villes' },
    { path: '/admin/hub-images', icon: FiImage, label: 'Photos maxula.ma' },

    { path: '/admin/performance', icon: FiBarChart2, label: 'Performance' },
    { path: '/admin/leakage', icon: FiTrendingDown, label: 'Fuite' },
    { path: '/admin/moderation', icon: FiAlertTriangle, label: 'Modération' },
    { path: '/admin/cgu', icon: FiShield, label: 'CGU' },
    { path: '/admin/pages', icon: FiFileText, label: 'Pages' },
    { path: '/admin/reviews', icon: FiMessageSquare, label: 'Avis' },
    { path: '/admin/payments', icon: FiDollarSign, label: 'Paiements' },
    { path: '/admin/withdrawals', icon: FiDownloadCloud, label: 'Retraits' },
    { path: '/admin/refund-requests', icon: FiRotateCcw, label: 'Remboursements' },
    { path: '/admin/disputes', icon: FiAlertCircle, label: 'Litiges' },
    { path: '/admin/referrals', icon: FiShare2, label: 'Parrainages' },
    { path: '/admin/analytics', icon: FiTrendingUp, label: 'Analyses' },
    { path: '/admin/marketing', icon: FiMail, label: 'Email Marketing' },
    { path: '/admin/settings', icon: FiSettings, label: 'Paramètres' },
    { path: '/admin/audit', icon: FiActivity, label: 'Audit Logs' },
    { path: '/admin/profile', icon: FiUser, label: 'Mon profil' }
  ];

  // ==========================================================================
  // MENU FILTRE PAR PERMISSIONS
  //
  // Un membre du personnel ne doit pas voir 40 entrees dont 35 lui renverront
  // une erreur. On lui presente uniquement ce qu'il peut ouvrir.
  //
  // ATTENTION : c'est du CONFORT D'AFFICHAGE, pas une securite. Le vrai verrou
  // est cote serveur, sur chaque route. Masquer un menu n'empeche personne de
  // taper l'URL — c'est le 403 du serveur qui l'arrete.
  //
  // Une entree absente de cette table reste reservee a l'admin : ajouter un
  // ecran sans l'y declarer le cache au personnel, ce qui est le bon defaut.
  // ==========================================================================
  const MENU_PERMISSION = {
    '/admin': 'stats.view',
    '/admin/deals': 'deals.view',
    '/admin/orders': 'orders.view',
    // v1.403.0 — ecran Clients ouvert au personnel (relances commerciales)
    '/admin/clients': 'customers.list',
    '/admin/merchants': 'merchants.view',
    '/admin/tickets': 'support.tickets',
    '/admin/reports': 'support.reports',
    '/admin/disputes': 'support.disputes',
    '/admin/vouchers': 'vouchers.view',
    '/admin/categories': 'content.categories',
    '/admin/brands': 'content.brands',
    '/admin/cities': 'content.cities',
    '/admin/hub-images': 'content.cities',
    '/admin/ads': 'content.ads',
    '/admin/pages': 'content.pages',
    '/admin/cgu': 'content.pages',
    '/admin/analytics': 'stats.view',
    '/admin/performance': 'stats.view',
    '/admin/profile': null, // toujours visible : son propre compte
    // /admin/staff et /admin/roles ne sont PAS declares : distribuer les droits
    // reste reserve a l'admin, sinon un compte pourrait s'auto-promouvoir.
  };

  const visibleMenuItems = useMemo(() => {
    if (!user || user.role === 'admin') return menuItems;
    const granted = Array.isArray(user.permissions) ? user.permissions : [];
    return menuItems.filter((item) => {
      if (!(item.path in MENU_PERMISSION)) return false; // non declare = admin seul
      const need = MENU_PERMISSION[item.path];
      return need === null || granted.includes(need);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;
    fetchUnreadCount();

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const endpoints = [
        '/admin/notifications/unread-count',
        '/notifications/unread-count',
        '/admin/notifications/count'
      ];

      let response = null;
      for (const ep of endpoints) {
        try {
          response = await API.get(ep);
          if (response?.data) break;
        } catch (err) {}
      }

      if (!response || !response.data) {
        if (!mountedRef.current) return;
        setUnreadCount(0);
        return;
      }

      const data = response.data;
      if (!mountedRef.current) return;
      setUnreadCount(Number(data.count || 0));
    } catch (error) {
      if (!mountedRef.current) return;
      setUnreadCount(0);
    }
  };

  const handleLogout = () => {
    try {
      if (typeof logout === 'function') logout();
    } catch (e) {}
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const formatBadge = (count) => {
    if (!count || count <= 0) return null;
    if (count > 99) return '99+';
    if (count > 9) return '9+';
    return String(count);
  };

  // Compteurs "à traiter" pour les badges de la sidebar admin.
  useEffect(() => {
    let alive = true;
    const fetchBadges = async () => {
      try {
        const { data } = await API.get('/admin/badges');
        const d = data?.data || {};
        if (alive) setBadges({
          merchants: Number(d.merchants) || 0,
          verifications: Number(d.verifications) || 0,
          tickets: Number(d.tickets) || 0,
          reports: Number(d.reports) || 0,
          refunds: Number(d.refunds) || 0,
          withdrawals: Number(d.withdrawals) || 0,
          subscriptions: Number(d.subscriptions) || 0,
          moderation: Number(d.moderation) || 0
        });
      } catch (e) {}
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Mappe un chemin de menu vers son compteur de badge.
  const badgeMap = {
    '/admin/merchants': badges.merchants,
    '/admin/verifications': badges.verifications,
    '/admin/tickets': badges.tickets,
    '/admin/reports': badges.reports,
    '/admin/subscriptions': badges.subscriptions,
    '/admin/withdrawals': badges.withdrawals,
    '/admin/refund-requests': badges.refunds,
    '/admin/moderation': badges.moderation,
    '/admin/unverified-emails': badges.unverifiedEmails
  };
  const fmtBadge = (n) => (!n || n <= 0) ? null : (n > 9 ? '9+' : String(n));

  return (
    <div className="bg-gray-100" style={{ zoom: adminZoom, minHeight: `calc(100vh / ${adminZoom})` }}>
      <nav className="fixed top-0 z-30 w-full bg-white border-b border-gray-200">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center justify-start shrink-0">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="inline-flex items-center p-2 text-gray-600 rounded-lg lg:hidden hover:bg-gray-100 focus:outline-none"
                aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              >
                {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
              <Link to="/admin" className="flex items-center ml-2 md:mr-6">
                {brandLogo ? (
                  <img src={brandLogo} alt={settings?.siteName || 'Logo'} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.outerHTML = `<span class='self-center text-xl font-bold text-primary-600 whitespace-nowrap'>${settings?.siteName || 'MAXULA'}</span>`; }} className="h-9 w-auto object-contain" />
                ) : (
                  <span className="self-center text-xl font-bold text-primary-600 whitespace-nowrap">
                    {settings?.siteName || 'MAXULA'}
                  </span>
                )}
                <span className="ml-2 text-sm font-normal text-gray-500 self-center hidden sm:inline">
                  Admin
                </span>
              </Link>
            </div>

            <div className="hidden md:block flex-1 max-w-3xl">
              <GlobalSearch scope="admin" />
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <select
                value={adminZoom}
                onChange={(e) => changeZoom(e.target.value)}
                title="Densité d'affichage"
                className="hidden sm:block text-xs text-gray-600 border border-gray-200 rounded-md px-1.5 py-1 mr-1 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer bg-white"
              >
                <option value="1">100%</option>
                <option value="0.9">90%</option>
                <option value="0.8">80%</option>
                <option value="0.75">75%</option>
              </select>
              {storeRef && (
                <GlowButton
                  variant="green"
                  href={`/merchants/${storeRef}`}
                  icon={FiExternalLink}
                  size="sm"
                  title="Voir ma boutique publique"
                  className="mr-1"
                >
                  <span className="hidden sm:inline">Ma boutique</span>
                </GlowButton>
              )}
              <Link
                to="/admin/notifications"
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Notifications"
              >
                <FiBell size={20} />
                {formatBadge(unreadCount) && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                    {formatBadge(unreadCount)}
                  </span>
                )}
              </Link>
              <ProfileMenu user={user} scope="admin" onLogout={handleLogout} />
            </div>
          </div>
        </div>
      </nav>

      <aside
        className={`fixed inset-y-0 left-0 z-20 w-64 bg-white border-r border-gray-200 pt-16 flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col flex-1 min-h-0">
          <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
            {visibleMenuItems.map((item) => {
              const active = isActive(item.path, item.exact);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <span className="flex-1">{item.label}</span>
                  {fmtBadge(badgeMap[item.path]) && (
                    <span className="ml-2 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {fmtBadge(badgeMap[item.path])}
                    </span>
                  )}
                </Link>
              );
            })}

            <Link
              to="/admin/notifications"
              className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive('/admin/notifications')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <div className="flex items-center">
                <FiBell className="w-5 h-5 mr-3" />
                Notifications
              </div>
              {formatBadge(unreadCount) && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                  {formatBadge(unreadCount)}
                </span>
              )}
            </Link>
          </nav>

          <div className="shrink-0 px-3 py-3 border-t bg-white space-y-1">
            <SidebarProfileCard
              user={user}
              scope="admin"
              to="/admin/profile"
              onNavigate={() => setSidebarOpen(false)}
            />
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <FiLogOut className="w-5 h-5 mr-3" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-10 bg-gray-900 bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="pt-16 lg:pl-64">
        <main className="p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
